import { rateTypeDisplayLabel } from 'src/utils/rateTypeIdentity';
import DocumentEditorColumns from './DocumentEditorColumns';
import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Stack,
  TableCell,
  TextField,
  Typography
} from '@mui/material';
import { APIClient } from 'src/helper/api_helper';
import { workspaceSessionStamp } from 'src/auth/workspaceSession';
import QuoteEditorRateOptions from './QuoteEditorRateOptions';
import { shouldIncludePerDiem, shouldIncludeMiscFees } from './quoteFeeColumns';

/** Old generated Actuals copied quote-response audit JSON into this field. */
export function actualsAdminNotesText(value: unknown): string {
  if (typeof value !== 'string') return '';
  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) &&
        Object.prototype.hasOwnProperty.call(parsed, 'clientResponseSubmission')) return '';
  } catch { /* Plain-text notes. */ }
  return value;
}

export const documentOptions = (document: any) => {
  const saved = document?.clientDocumentOptions ?? {};
  return {
    includeArea: saved.includeArea ?? false,
    includeToolsGear: saved.includeToolsGear ?? false,
    includePerDiem: saved.includePerDiem ?? shouldIncludePerDiem(document),
    includeMiscFees: saved.includeMiscFees ?? shouldIncludeMiscFees(document),
    contractTerms: saved.contractTerms ?? '',
    rateTypesNotes: saved.rateTypesNotes ?? '',
    notes: saved.notes ?? ''
  };
};
export function useDocumentOptions(document: any) {
  const [options, setOptions] = useState(() => documentOptions(document));
  useEffect(() => {
    setOptions(documentOptions(document));
  }, [document?.id, document?.clientDocumentOptions]);
  return { options, setOptions };
}
/** Keep private autosave private; the explicit draft action retains the existing
 * permitted display-options save and its canonical-revision refresh. */
export function useDocumentDraftSave(kind: 'actuals' | 'invoice', { id, options, canonical, adminNotes, readOnly, account }: any) {
  const withAdminNotes = kind === 'actuals' && typeof adminNotes === 'string';
  const saveValues = (settings: any, notes: string) => ({ ...documentOptions({ clientDocumentOptions: settings }),
    ...(withAdminNotes ? { adminNotes: notes } : {}) });
  const baseline = JSON.stringify(saveValues(documentOptions(canonical), actualsAdminNotesText(canonical?.adminNotes)));
  const identity = `${kind}:${id}:${workspaceSessionStamp()}:${readOnly}`;
  const persisted = useRef(baseline);
  const inFlight = useRef(false);
  const generation = useRef(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    generation.current++;
    persisted.current = baseline;
    inFlight.current = false;
    setSaving(false);
    setError('');
    return () => { generation.current++; };
  }, [identity, baseline]);
  const save = async () => {
    if (!account.ready || inFlight.current) return;
    const current = generation.current;
    inFlight.current = true;
    setSaving(true);
    setError('');
    try {
      const selected = saveValues(options, adminNotes);
      const signature = JSON.stringify(selected);
      if (!readOnly && signature !== persisted.current) {
        // Validate/recover the private optimistic revision before changing the
        // canonical options. A failed or unresolved conflict stays visible.
        const checkpoint = await account.retry();
        if (!checkpoint || current !== generation.current) return;
        const payload = { ...selected };
        if (withAdminNotes && selected.adminNotes === JSON.parse(persisted.current).adminNotes) delete payload.adminNotes;
        await account.runOperational(() => new APIClient().create(
          kind === 'actuals' ? `/actuals/document-options/${id}` : `/jobs/clientInvoice/${id}/document-options`, payload, {}
        ), true);
        if (current !== generation.current) return;
        persisted.current = signature;
      }
      await account.saveVersion();
    } catch (failure) {
      if (current === generation.current) setError((failure as any)?.message || 'Unable to save document options. Your edits remain open.');
    } finally {
      if (current === generation.current) {
        inFlight.current = false;
        setSaving(false);
      }
    }
  };
  return { save, saving, error, hasOptionChanges: !readOnly && JSON.stringify(saveValues(options, adminNotes)) !== persisted.current };
}

export function DocumentOptionsEditor({
  options,
  onChange,
  disabled = false,
  showVisibilityHint = true,
  showDocumentNotes = true
}: any) {
  const change = (key: string, value: any) => onChange({ ...options, [key]: value });
  return (
    <Box sx={{ mx: { xs: 2, md: 6 }, my: 2 }} data-html2canvas-ignore="true">
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        useFlexGap
        flexWrap="wrap"
        gap={2}
      >
        <QuoteEditorRateOptions
          showArea={options.includeArea}
          showToolsGear={options.includeToolsGear}
          includePerDiem={options.includePerDiem}
          includeMiscFees={options.includeMiscFees}
          disabled={disabled}
          onShowAreaChange={(v) => change('includeArea', v)}
          onShowToolsGearChange={(v) => change('includeToolsGear', v)}
          onIncludePerDiemChange={(v) => change('includePerDiem', v)}
          onIncludeMiscFeesChange={(v) => change('includeMiscFees', v)}
        />

      </Stack>
      {showVisibilityHint && <Typography variant="caption">
        Column visibility does not change charges.
      </Typography>}
      {showDocumentNotes && <Box sx={{ border: '1px solid #dbbe00', mt: 2 }}>
        <Typography
          component="h3"
          sx={{
            m: 0,
            p: 1.25,
            background: '#e9d8a6',
            fontSize: 13,
            fontWeight: 700,
            color: '#000'
          }}
        >
          Contract Terms &amp; Rate Types
        </Typography>
        <Stack spacing={2} sx={{ p: 1.5 }}>
          {[
            ['contractTerms', 'Document Contract Terms'],
            ['rateTypesNotes', 'Rate Types Notes'],
            ['notes', 'Document Notes']
          ].map(([key, label]) => (
            <TextField
              key={key}
              label={label}
              multiline
              minRows={2}
              value={options[key]}
              onChange={(e) => change(key, e.target.value)}
              disabled={disabled}
              inputProps={{ maxLength: 20000 }}
            />
          ))}
        </Stack>
      </Box>}

    </Box>
  );
}
export function DocumentContractBlock({ options, positions = [], rateTypeOptions = [] }: any) {
  const rates = Array.from(
    new Set(positions.map((row: any) => rateTypeDisplayLabel(row.rateType,
      row.rateTypeLabel || rateTypeOptions.find((option: any) => option.value === row.rateType)?.label)).filter(Boolean))
  ).join(' · ');
  return (
    <Box
      aria-label="Document contract terms and notes"
      data-document-contract
      sx={{
        mx: { xs: 2, md: 6 },
        my: 2,
        border: '1px solid #dbbe00',
        color: '#000',
        breakInside: 'avoid'
      }}
    >
      <Typography
        component="h3"
        sx={{
          m: 0,
          p: 1.25,
          background: '#e9d8a6',
          fontWeight: 700,
          fontSize: 13
        }}
      >
        Contract Terms &amp; Rate Types
      </Typography>
      <Box
        sx={{
          p: 1.5,
          fontSize: 13,
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere'
        }}
      >
        <Typography variant="body2">
          <strong>Rate Types:</strong> {rates || 'No rate types recorded.'}
        </Typography>
        {options.contractTerms && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Contract Terms:</strong>
            {'\n'}
            {options.contractTerms}
          </Typography>
        )}
        {options.rateTypesNotes && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Rate Types Notes:</strong>
            {'\n'}
            {options.rateTypesNotes}
          </Typography>
        )}
        {options.notes && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Notes:</strong>
            {'\n'}
            {options.notes}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
export function DocumentExtraCell({ position, field }: any) {
  return (
    <TableCell
      align="center"
      sx={{
        border: '1px solid #dbbe00',
        fontSize: 13,
        color: '#000',
        whiteSpace: 'normal',
        overflowWrap: 'anywhere'
      }}
    >
      {position?.[field] || position?.Shift?.[field] || '—'}
    </TableCell>
  );
}
/** Retained export for legacy callers; all document rows use the same active column plan. */
export function DocumentColgroup({ options }: any) {
  return <DocumentEditorColumns options={options} />;
}
export const documentTableSx = (options: any) => ({
  tableLayout: 'fixed',
  width: '100%',
  minWidth: 0,
  '& th, & td': {
    borderWidth: '1px !important',
    borderColor: '#dbbe00 !important'
  },
  // Summary rows have fewer cells; their final amount is never the Notes column.
  [`& tbody tr > td:nth-of-type(${5 + Number(options.includeArea) + Number(options.includeToolsGear)}):not(:last-child)`]: { whiteSpace: 'normal !important', overflowWrap: 'anywhere', textAlign: 'left' },
  '& tbody tr > td:last-child, & tbody tr > td:last-child .MuiTypography-root': { whiteSpace: 'nowrap', overflowWrap: 'normal', wordBreak: 'normal' },
  '& tbody tr > td:last-child .MuiTypography-root': { fontSize: 'inherit' },
  '& thead th': { width: 'auto !important', minWidth: '0 !important', textTransform: 'none' }
});
